<template>
  <div>
    <b-navbar v-if="getBookingNumber" toggleable="lg" type="dark" variant="dark">
      <!-- Hamburger menu button -->
      <b-navbar-toggle left target="nav-collapse">
        <i class="material-icons hamburger-button p-2">
          <span>&#xE5D2;</span>
        </i>
      </b-navbar-toggle>

      <b-navbar-brand class="d-block d-lg-none">
        <img class="vtd-d-inline-block vtd-align-top navbar-logo"
          src="https://cdn.trailfinders.com/vt-logo-light-tricolour-simple.png?width=450">
      </b-navbar-brand>

      <!-- Show Only Logo for Friends & Family -->
      <b-collapse v-if="isFriendFamily" is-nav>
        <b-navbar-nav class="d-none d-sm-none d-lg-block">
          <img class="viewtrail-logo" src="https://cdn.trailfinders.com/vt-logo-light-tricolour-simple.png?width=450">
        </b-navbar-nav>
      </b-collapse>

      <b-collapse v-else id="nav-collapse" is-nav>
        <!-- Left menu -->
        <b-col class="col-sm-4 left-col px-0">
          <b-navbar-nav>
            <!-- Booking Number -->
            <div class="nav-main-item reference">
              <!-- Settings icon -->
              <div class="link-content-container mb-4">
                <div class="booking-number">
                  <div v-if="onItineraryPage" id="reference-number">
                    <span class="ml-4 mr-2">Trailfinders Reference:</span>
                    <span>
                      <strong>{{ getBookingNumber }}</strong>
                    </span>
                  </div>
                  <router-link v-else id="reference-number" :to="landingLink">
                    <span class="ml-4 mr-2">Trailfinders Reference:</span>
                    <span class="booking-no">
                      <strong>{{ getBookingNumber }}</strong>
                    </span>
                  </router-link>
                </div>
              </div>
            </div>
          </b-navbar-nav>
        </b-col>

        <b-col class="col-sm-4 center-col px-0">
          <b-navbar-nav class="d-none d-sm-none d-lg-block">
            <img class="viewtrail-logo" :class="{ 'logo-centered': getEmails.length >= 1 }"
              src="https://cdn.trailfinders.com/vt-logo-light-tricolour-simple.png?width=450">
          </b-navbar-nav>
        </b-col>

        <!-- Right sided content -->
        <b-col class="col-sm-4 right-side-col px-0" :class="[
          { 'justify-content-around': isInternal },
          { 'justify-content-end': !isInternal },
        ]">
          <div v-if="isInternal" class="int-warning-btn btn d-flex flex-column" @click="reportAnIssue">
            <p class="int-warning-text m-0">
              Current view: INTERNAL
            </p>
            <p class="int-warning-report m-0">
              Click to report a VT issue
            </p>
          </div>
          <b-navbar-nav class="dropdown-central">
            <!-- bell icon -->
            <b-nav-item-dropdown v-if="getEmails.length >= 1 || getPredepShow" no-caret right class="nav-main-item">
              <template #button-content>
                <div class="link-content-container">
                  <div v-if="hasUnreadEmails" class="notification" />
                  <div>
                    <i class="material-icons ml-4 mr-5">
                      <span>&#xE7F5;</span>
                    </i>
                    <span class="d-lg-none">Notifications</span>
                  </div>
                  <b-icon class="d-lg-none mr-4 ml-4 caret-icon" icon="caret-down-fill" />
                </div>
              </template>
              <b-dropdown-item v-if="getPredepShow" class="nav-dropdown-item" @click="onPredepShow">
                Pre-Departure Information
              </b-dropdown-item>
              <!-- Loop over any notifications -->
              <b-dropdown-item v-for="email in getEmails" :key="email._id" class="nav-dropdown-item"
                @click="displayEmail(email)">
                Notification Sent: {{ email.created | formatDate }}
              </b-dropdown-item>
            </b-nav-item-dropdown>

            <!-- Settings -->
            <b-nav-item-dropdown v-if="!hideSettings" no-caret right class="nav-main-item">
              <!-- Using 'button-content' slot -->
              <template #button-content>
                <!-- Settings icon -->
                <div class="link-content-container">
                  <div>
                    <i id="settings-i" class="icon-settings ml-4 mr-5" />
                    <span class="d-lg-none">Settings</span>
                  </div>
                  <b-icon class="d-lg-none mr-4 ml-4 caret-icon" icon="caret-down-fill" />
                </div>
              </template>
              <b-dropdown-item-button class="nav-dropdown-item" @click.prevent="() => $refs.LoginTokenRef.authCheckGo('/profile')
                ">
                My Profile
              </b-dropdown-item-button>

              <b-dropdown-item-button class="nav-dropdown-item" @click.prevent="() => {
                $refs.LoginTokenRef.authCheckResult(() => {
                  $refs.AllBookingsRef.show();
                });
              }
                ">
                All Bookings
              </b-dropdown-item-button>

              <b-dropdown-item-button class="nav-dropdown-item" @click="goToTFSite">
                Trailfinders.{{ getBookingNumber.charAt(0).toUpperCase() === 'R' ? 'ie' : 'com' }}
              </b-dropdown-item-button>

              <b-dropdown-item-button class="nav-dropdown-item" @click="onLogout">
                Log Out
              </b-dropdown-item-button>
            </b-nav-item-dropdown>
          </b-navbar-nav>
        </b-col>
      </b-collapse>
    </b-navbar>

    <PreDepartureInfo ref="PreDepartureInfoRef" />
    <TravelHealth ref="TravelHealthRef" />
    <Visa ref="VisaRef" />
    <LoginToken ref="LoginTokenRef" />
    <AllBookings ref="AllBookingsRef" />
    <EmailNotification ref="EmailNotificationRef" />
    <ForwardFamilyFriends ref="ForwardFamilyFriendsRef" />
  </div>
</template>

<script>
import dayjs from '@/lib/dayjs';
import { mapActions, mapGetters, mapState } from "vuex";
import PreDepartureInfo from "@/modules/menu-bar/pre-departure-info";
import TravelHealth from "@/modules/menu-bar/essential-info/travel-health";
import Visa from "@/modules/menu-bar/essential-info/visa";
import LoginToken from "@/modules/auth/login-token";
import EmailNotification from "@/modules/menu-bar/email-notification";
import ForwardFamilyFriends from "@/modules/menu-bar/forward-family";
import AllBookings from "@/modules/all-bookings";
export default {
  components: {
    PreDepartureInfo,
    TravelHealth,
    Visa,
    LoginToken,
    EmailNotification,
    ForwardFamilyFriends,
    AllBookings
  },
  filters: {
    formatDate: function (value) {
      return dayjs(value).format("DD-MMM-YYYY HH:mm");
    },
  },
  props: {
    hideSettings: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      onItineraryPage: false,
      source: "EXT",
    };
  },
  async mounted() {
    if (this.$route.params.source) {
      this.source = this.$route.params.source.toUpperCase();
    }
    if (this.$route.path === "/booking") {
      this.onItineraryPage = true;
    }
    // External PDI Links to pop modals
    if (this.$route.params.component === "predep" && this.getPredepShow) {
      this.$refs.PreDepartureInfoRef.showModal();
    }

    if (this.$route.params.component === "no_cost") {
      this.$refs.ForwardFamilyFriendsRef.showModal();
    }

    for (const [index, item] of this.essentialInfoMenu.entries()) {
      if (item.partial === "VisaRef") {
        if (this.$route.params.component === "visaadvice") {
          this.$refs.VisaRef.showModal(this.essentialInfoMenu[index]);
        }

        this.$root.$on("visaadvice", () => {
          if (
            this.$refs &&
            this.$refs.VisaRef &&
            this.$refs.VisaRef.showModal
          ) {
            this.$refs.VisaRef.showModal(this.essentialInfoMenu[index]);
          }
        });
      }
      if (item.partial === "TravelHealthRef") {
        if (this.$route.params.component === "travelhealth") {
          this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index]);
        }

        this.$root.$on("travelhealth", () => {
          if (
            this.$refs &&
            this.$refs.TravelHealthRef &&
            this.$refs.TravelHealthRef.showModal
          ) {
            this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index]);
          }
        });
      }
    }
  },
  updated() {
    // External PDI Links to pop modals
    if (this.$route.params.component === "predep" && this.getPredepShow) {
      this.$refs.PreDepartureInfoRef.showModal();
    }
    if (this.$route.params.component === "no_cost") {
      this.$refs.ForwardFamilyFriendsRef.showModal();
    }

    for (const [index, item] of this.essentialInfoMenu.entries()) {
      if (item.partial === "VisaRef") {
        if (this.$route.params.component === "visaadvice") {
          this.$refs.VisaRef.showModal(this.essentialInfoMenu[index]);
        }
        this.$root.$on("visaadvice", () => {
          this.$refs.VisaRef.showModal(this.essentialInfoMenu[index]);
        });
      }

      if (item.partial === "TravelHealthRef") {
        if (this.$route.params.component === "travelhealth") {
          this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index]);
        }
        this.$root.$on("travelhealth", () => {
          if (
            this.$refs &&
            this.$refs.TravelHealthRef &&
            this.$refs.TravelHealthRef.showModal
          ) {
            this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index]);
          }
        });
      }
    }
  },
  computed: {
    ...mapState({
      booking: (state) => state.booking.booking,
    }),
    ...mapGetters({
      getBookingNumber: "booking/getBookingNumber",
      getPredepShow: "booking/getPredepShow",
      hasBookingSummary: "booking/hasBookingSummary",
      hasUnreadEmails: "booking/hasUnreadEmails",
      getEmails: "booking/getEmails",
      getFil: "booking/getFil",
      isInternal: "getInternal",
      userType: "auth/getUserType",
      essentialInfoMenu: "essentialInfoMenu/getMenu",
      isFriendFamily: "friendFamily/isFriendFamily"
    }),
    landingLink() {
      if (this.isFriendFamily) {
        return "/login";
      }
      if (this.userType === "corporate") {
        return "/landing-corp";
      }
      return "/booking";
    }
  },
  methods: {
    ...mapActions({ logout: "auth/logoutAction" }),
    ...mapActions("booking", ["markNotificationsRead"]),
    ...mapActions({
      login: "auth/loginBookingNumberAction",
    }),
    onLogout() {
      this.logout();
    },
    onPredepShow() {
      this.$refs.PreDepartureInfoRef.showModal();
    },
    goToTFSite() {
      let url = `https://www.trailfinders.${this.getBookingNumber.charAt(0).toUpperCase() === "R" ? "ie" : "com"}`;
      window.open(url, "_blank");
    },
    reportAnIssue() {
      window.open(
        `https://myintranet.trailfinders.com/webwork/feedback.nsf/printedTravelPlanFeedback?ReadForm#/vt-form?bookingref=${this.getBookingNumber}`,
        "_blank"
      );
    },
    displayEmail(email) {
      // Mark Notifications Read
      this.markNotificationsRead();
      this.$refs.EmailNotificationRef.showModal(email);
    },
    returnPrintBookingTitle() {
      //If FIL = M then Receipt else Invoice
      // Actual display of link for Booking Invoice / Booking Receipt for printing depends on getter - hasBookingSummary
      if (this.getFil === "M") {
        return "Receipt";
      }
      return "Invoice";
    },
  },
};
</script>
<style lang="scss">
.center-col {
  display: flex;
  justify-content: center;
}

.right-side-col {
  display: flex;
}

.int-warning-btn {
  background-color: #fcb134;
  border-radius: 3px;
  padding: 5px 30px;
}

.int-warning-text {
  color: #fff;
  padding-bottom: 5px;
}

// Toggle button
.hamburger-button {
  color: whitesmoke;
  transition: all 0.2s;
  border: none !important;
}

.hamburger-button:hover {
  color: rgba(245, 245, 245, 0.356);
  cursor: pointer;
}

.navbar-dark .navbar-toggler {
  border: none !important;
}

.navbar img {
  margin: 0;
}

#reference-number {
  text-decoration: none !important;
}

#notifications-i {
  font-size: 1.8rem;
}

#settings-i {
  font-size: 1.4rem;
  color: #fff;
}

// Overall nav styles

nav.navbar.navbar-dark.bg-dark.navbar-expand-lg {
  min-height: 70px;
  padding: 12px 0;
  background-color: #434343 !important;

  span {
    font-weight: 100;
    color: #fff;
  }
}

.navbar-nav {
  transition: all 1s;
}

// Nav items

.nav-main-item {
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.651);
}

.navbar-logo {
  height: 30px;
}

.link-content-container {
  display: flex;
  align-items: center;
  justify-content: center;

  div {
    display: flex;
    align-items: center;
  }
}

.booking-no {
  color: #fcb134 !important;
}

.essential-info-menu {
  min-width: 300px;
}

.sub-nav-icon {
  position: absolute;
  right: 10px;
  bottom: 5px;
  font-size: 30px;
}

.caret-icon {
  font-size: 100% !important;
  color: #fff;
}

.booking-number {
  span {
    font-weight: 500 !important;
  }
}

// Dropdowns
ul.dropdown-menu {
  position: relative !important;
  top: 10px !important;
  padding: 0;
  border-radius: 0;
}

.nav-dropdown-item {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  background-color: #f5f5f5;
  padding: 15px 30px;
  border-bottom: 1px solid #4545453b;
  position: relative;
  width: 100%;

  span {
    color: #000 !important;
  }
}

.dropdown-item {
  padding: 0;
}

#dropdown-2 .nav-dropdown-item {
  border: none;
  background-color: #fff;
}

.nav-dropdown-item:hover {
  background-color: #a8a8a8;
}

.dropdown-item:hover {
  background-color: #a8a8a8;
}

#dropdown-2 .nav-dropdown-item:hover {
  background-color: #a8a8a8;
}

.reference {
  padding: 10px 0px;
}

// Media queries
@media only screen and (min-width: 992px) {

  // Overall navbar styles
  nav.navbar.navbar-dark.bg-dark.navbar-expand-lg {
    max-height: 70px;
  }

  .navbar-expand-lg .navbar-collapse {
    display: flex;
    justify-content: space-between;
  }

  // Nav items
  .nav-main-item {
    padding: 0;
    border-bottom: none;
  }

  li.nav-item {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  // Dropdowns
  .b-nav-dropdown {
    display: flex;
    align-items: center;
  }

  ul.dropdown-menu {
    position: absolute !important;
    top: 63px !important;
  }

  .logo-centered {
    margin-left: 6rem;
  }
}

@media only screen and (min-width: 1341px) {
  ul.dropdown-menu {
    top: 52px !important;
  }
}

/* Notification style */
.notification {
  position: absolute;
  top: 15px;
  left: 40px;
  height: 12px;
  width: 12px;
  background-color: #e61c2e;
  border-radius: 100%;
}

@media only screen and (min-width: 992px) {
  .notification {
    left: 45px;
    top: 15px;
  }
}

@media only screen and (max-width: 992px) {
  .nav-main-item {
    border: none;
  }

  // #__BVID__4405 {
  //   display: none;
  // }
  // col-sm-4 caps these at 33% width from 576px up - override so each
  // section fills the expanded mobile menu instead of being squeezed to one side
  .left-col,
  .center-col,
  .right-side-col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .right-side-col {
    justify-content: center !important;
  }

  .int-warning-btn {
    display: none !important;
  }

  // dropdown-central otherwise shrinks to fit its content, leaving
  // right-side-col's justify-content to park it on one side instead of
  // letting Notifications/Settings span the full width of the menu
  .dropdown-central {
    width: 100%;
    margin-top: -40px;
  }

  .dropdown-central .link-content-container {
    justify-content: center;
    padding: 0 20px;
  }

  ul.dropdown-menu {
    text-align: center;
  }
}
</style>
